import { type CSSProperties, type RefObject, useEffect, useRef } from "react";

/**
 * Suppresses the native scrollbar so the floating thumb overlays content instead of taking layout
 * width. Pair with {@link SCROLL_SURFACE_CLASS}, which hides the WebKit pseudo-element.
 */
const HIDE_NATIVE_SCROLLBAR: CSSProperties = { scrollbarWidth: "none" };

/**
 * Fades out the last of the content when it is clipped, so a cut-off list reads as "there is more"
 * rather than as a rendering glitch. A mask rather than a gradient overlay so it composes with any
 * background (scroll containers that change colour on hover would mismatch a hardcoded overlay).
 * Tailwind 3 has no mask utilities, hence the inline style.
 */
const FADE_MASK: CSSProperties = {
  maskImage: "linear-gradient(to top, transparent 0, black 2rem)",
  WebkitMaskImage: "linear-gradient(to top, transparent 0, black 2rem)",
};

/** Companion to {@link scrollSurfaceStyle} — hides the WebKit scrollbar pseudo-element. */
export const SCROLL_SURFACE_CLASS = "[&::-webkit-scrollbar]:hidden";

/** Fades a scroll container's bottom edge when its content is clipped. Leaves the scrollbar alone. */
export const fadeStyle = ({ faded }: { faded: boolean }): CSSProperties => (faded ? FADE_MASK : {});

/** Styles a floating-scrollbar scroll container, optionally fading its bottom edge when clipped. */
export const scrollSurfaceStyle = ({ faded }: { faded: boolean }): CSSProperties => ({
  ...HIDE_NATIVE_SCROLLBAR,
  ...fadeStyle({ faded }),
});

/** The overlaid thumb {@link useFloatingScrollbar} drives imperatively (height, offset, opacity). */
export const FLOATING_THUMB_CLASS =
  "absolute top-0 right-1 w-1.5 rounded-full bg-muted-foreground/50 pointer-events-none opacity-0 transition-opacity duration-300";

export interface UseFloatingScrollbarOptions {
  hideDelayMs?: number;
  minThumbHeight?: number;
  trackInsetTopRef?: RefObject<HTMLElement | null>;
}

export interface UseFloatingScrollbarResult<Container extends HTMLElement = HTMLDivElement> {
  scrollContainerRef: RefObject<Container | null>;
  thumbRef: RefObject<HTMLDivElement | null>;
  handleScroll: () => void;
}

/**
 * Provides refs and a scroll handler for a custom overlay scrollbar that floats
 * over the scroll container's content. The thumb fades in on scroll and fades
 * out after `hideDelayMs` of inactivity. All updates happen via direct DOM
 * mutation, so scrolling does not trigger React re-renders.
 *
 * Attach `scrollContainerRef` to the scrollable element and pass `handleScroll`
 * to its `onScroll` prop. Attach `thumbRef` to an absolutely-positioned thumb
 * element layered over the scroll container.
 */
export const useFloatingScrollbar = <Container extends HTMLElement = HTMLDivElement>(
  options?: UseFloatingScrollbarOptions
): UseFloatingScrollbarResult<Container> => {
  const hideDelayMs = options?.hideDelayMs ?? 800;
  const minThumbHeight = options?.minThumbHeight ?? 30;

  const scrollContainerRef = useRef<Container>(null);
  const thumbRef = useRef<HTMLDivElement>(null);
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleScroll = () => {
    const el = scrollContainerRef.current;
    const thumb = thumbRef.current;
    if (!el || !thumb) return;
    const { scrollTop, scrollHeight, clientHeight } = el;
    if (scrollHeight <= clientHeight) {
      thumb.style.opacity = "0";
      return;
    }
    const inset = options?.trackInsetTopRef?.current?.offsetHeight ?? 0;
    const height = Math.max((clientHeight / scrollHeight) * clientHeight, minThumbHeight);
    const availableTrack = Math.max(clientHeight - inset - height, 0);
    const top = inset + (scrollTop / (scrollHeight - clientHeight)) * availableTrack;
    thumb.style.height = `${height}px`;
    thumb.style.transform = `translateY(${top}px)`;
    thumb.style.opacity = "1";
    if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
    hideTimeoutRef.current = setTimeout(() => {
      if (thumbRef.current) thumbRef.current.style.opacity = "0";
    }, hideDelayMs);
  };

  useEffect(() => {
    return () => {
      if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
    };
  }, []);

  return { scrollContainerRef, thumbRef, handleScroll };
};
